<template>
  <div v-if="records.length === 0" class="p-movies">
    <div class="container">
      <div class="p-movies__empty">
        <i class="icon-camera"></i>
        <span>
          {{ dictionary.emptyRecordsText }}
        </span>
      </div>
    </div>
  </div>
  <div v-else class="p-movies__outerwrapper">
    <section class="p-movies__header-container">
      <div class="container">
        <div class="p-movies__header">
          <div class="p-movies__header__el-holder p-movies__header__display">
            <div class="b-search-input b-search-input--no-btn">
              <input
                v-model="searchMovieTitle"
                class="e-search"
                type="text"
                :placeholder="dictionary.movieSearch"
                @input="startSearchMovies" />
            </div>
            <div class="b-movies__view-options">
              <LayoutViewSwitch v-show="desktop" @change-view="changeView" />
            </div>
          </div>
          <!-- filters for mobile view -->
          <div class="p-movies__header__mobile-filters">
            <div class="b-search-input b-search-input--no-btn">
              <input
                v-model="searchMovieTitle"
                class="e-search"
                type="text"
                :placeholder="dictionary.movieSearch"
                @input="startSearchMovies" />
            </div>

            <button
              type="button"
              class="s-playing-now__filters-btn"
              @click="() => (showMobileFiltersModal = true)"></button>
          </div>
        </div>
      </div>
    </section>

    <section
      :class="
        currentView === 'horizontal'
          ? 'p-movies s-strip'
          : 'p-movies__vertical s-strip'
      ">
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-2 col-xl-2 p-movies__right-col">
            <Sticky>
              <PlayingNowFilters
                key="desktop-PlayingNowFilters"
                :where-do-you-want="dictionary.whereDoYouWant"
                :what-do-you-want="dictionary.whatDoYouWant"
                :clear-filters="dictionary.clearFilter"
                :all-genres="dictionary.allGenres"
                :placeholders="filterPlaceholders"
                :selected-filters="selectedFilters"
                :show-filters="showFilters"
                :filters="filtersData"
                @filter-changed="updateSelectedFilters"
                @reset-filters="resetMoviesFilters"></PlayingNowFilters>
            </Sticky>
          </div>
          <div
            v-if="currentView === 'horizontal'"
            class="col-12 col-lg-10 col-xl-9 offset-sm-0 offset-xl-1">
            <div class="row">
              <div
                v-for="(record, index) in filteredRecords"
                :key="index"
                class="col-12 col-sm-6 col-md-4">
                <MovieCard
                  :title="record.title"
                  :subtitle="record.subTitle"
                  :description="record.desc"
                  :image="record.thumb"
                  :pg="record.pg"
                  :duration="record.dur"
                  :dolby="record.dolby"
                  :three-dimensions="record.threeDimensions"
                  :imax="record.imax"
                  :imax3D="record.imax3D"
                  :sphera="false"
                  :genres="recordGenres(record)"
                  :dictionary="dictionary"
                  :due="record.due"
                  :video-id="record.vid"
                  :play="false"
                  :movie-id="record.movieId"
                  :transl="record.transl"
                  :movie-url="record.url"
                  :cinema-id="selectedCinemaId"
                  :only-members="record.onlyMembers"
                  :elements-show-options="{
                    showPremier: false,
                    showTrailerCta: false,
                    showBookingCta: true,
                    showDuration: true,
                    showGenres: true,
                    showDesc: false,
                    showMoreCta: true,
                    posterCardDimensions: {},
                  }"></MovieCard>
              </div>
            </div>
          </div>
          <div v-else class="col-12 col-lg-9 offset-lg-1">
            <div class="row">
              <div
                v-for="(record, index) in filteredRecords"
                :key="index"
                class="col-12 col-sm-6 col-md-12">
                <MovieHorizontalCard
                  :key="record.id"
                  :title="record.title"
                  :description="record.desc"
                  :image="record.thumb"
                  :pg="record.pg"
                  :duration="record.dur"
                  :genres="recordGenres(record)"
                  :dictionary="dictionary"
                  :sub-title="record.subTitle"
                  :dolby="record.dolby"
                  :three-dimensions="record.threeDimensions"
                  :imax="record.imax"
                  :imax3d="record.imax3D"
                  :sphera="false"
                  :movie-id="record.movieId"
                  :movie-url="record.url"
                  :cinema-id="selectedCinemaId" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <ModalOverlay v-model="showMobileFiltersModal" size="player">
      <template #header>
        <i class="icon-filters"></i>
        <div class="b-pop-up__header__title">{{ dictionary.filters }}</div>
      </template>
      <template #body>
        <div class="b-pop-up__content">
          <PlayingNowFilters
            key="desktop-PlayingNowFilters"
            :where-do-you-want="dictionary.whereDoYouWant"
            :what-do-you-want="dictionary.whatDoYouWant"
            :clear-filters="dictionary.clearFilter"
            :all-genres="dictionary.allGenres"
            :placeholders="filterPlaceholders"
            :selected-filters="selectedFilters"
            :show-filters="showFilters"
            :filters="filtersData"
            @filter-changed="updateSelectedFilters"
            @reset-filters="resetMoviesFilters"></PlayingNowFilters>

          <div class="b-pop-up__btn-area">
            <button
              type="submit"
              class="b-pop-up__btn-mobile"
              @click="closeFilterModal">
              {{ dictionary.result }}
            </button>
            <button
              type="reset"
              class="btn-cta b-pop-up__btn-reset"
              @click="resetMoviesFilters">
              {{ dictionary.clearFilter }}
            </button>
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeMount, onMounted, ref, watch } from 'vue'
import useViewport from '../../features/viewport'
import LayoutViewSwitch from '../components/LayoutViewSwitch.vue'
import MovieHorizontalCard from '../components/MovieHorizontalCard.vue'
import MovieCard from '../components/MovieCard.vue'
import ModalOverlay from '../components/ModalOverlay.vue'
import PlayingNowFilters from '../components/PlayingNowFilters.vue'
import Sticky from '../components/UI/Sticky.vue'

const { desktop } = useViewport()
import { getData, moveWindowToObj } from '../../lib/lib'
// import { getMonthName } from '../../lib/date'
import jquery from 'jquery'
const { filters, records: movies, dictionary } = getData('movieOverviewData')

watch(desktop, () => {
  if (desktop.value) {
    closeFilterModal()
  }
})

const placeholderCinemas = dictionary.placeholderCinemas
  ? dictionary.placeholderCinemas
  : 'All'
const placeholderRoomType = dictionary.placeholderRoomType
  ? dictionary.placeholderRoomType
  : 'All'
const placeholderSreenAttr = dictionary.placeholderSreenAttr
  ? dictionary.placeholderSreenAttr
  : 'All'

const filterPlaceholders = computed(() => {
  return [placeholderCinemas, placeholderSreenAttr, placeholderRoomType]
})

const currentView = ref('horizontal')
const records = ref([])
const filteredRecords = ref([])
const filtersData = ref({})
const filtersDataFiltered = ref({})

const selectedFiltersIniValue = {
  cinemas: 'All',
  roomTypes: 'All',
  screenTypes: 'All',
  dates: 'All',
  // dates: filters.dates[0] ? filters.dates[0].value : [],
  genres: [],
}

const selectedFilters = ref({ ...selectedFiltersIniValue })
const genres = ref([])
const showMobileFiltersModal = ref(false)

const searchMovieTitle = ref('')
const moviesFiltersData = ref([])

const showFilters = computed(() => {
  return ['cinemas', 'screenTypes', 'roomTypes']
})

const selectedCinemaId = computed(() => {
  return selectedFilters.value.cinemas === 'All'
    ? ''
    : selectedFilters.value.cinemas
})
// const currentMonthName = computed(() => {
//   return getMonthName(new Date(), 'el')
// })

onBeforeMount(() => {
  genres.value = [...filters.genres]
  records.value = movies
  filtersData.value = { ...filters }
  filtersDataFiltered.value = { ...filters }
  filteredRecords.value = movies
  moviesFiltersData.value = filters.movies
})

onMounted(() => {
  const selectedCinema = filters.cinemas.find((c) => c.isSelected)
  const selectedGenres = filters.genres
    .filter((g) => g.isSelected)
    .map((g) => g.value)
  selectedFilters.value = {
    ...selectedFiltersIniValue,
    cinemas: selectedCinema?.value || 'All',
    genres: selectedGenres,
  }
  filterChanged(selectedFilters.value, false)
})

const recordGenres = (record) => {
  return [record.genre]
}

const filterChanged = async (filters, scroll = true) => {
  if (Object.values(filters).every((filter) => filter === 'All')) {
    filteredRecords.value = records.value
  } else {
    filteredRecords.value = filterMoviesData(filters)
  }
  await nextTick()
  if (scroll) {
    moveWindowToObj(jquery('.p-movies__outerwrapper'))
  }
}

const filterMoviesData = (filters) => {
  const filteredRecords = records.value.filter((record) => {
    const cinemaMatch =
      filters.cinemas === 'All' || record.cinemas.includes(filters.cinemas)

    const roomTypeMatch =
      filters.roomTypes === 'All' ||
      record.roomTypes.includes(filters.roomTypes)

    const screenTypeMatch =
      filters.screenTypes === 'All' ||
      record.screenTypes.includes(filters.screenTypes)

    const selectedDay = filtersData.value['dates'].find(
      (day) => day.value === filters.dates,
    )

    // const dateMatch = record.dates.includes(selectedDay.value)
    const dateMatch =
      filters.dates === 'All' || record.dates.includes(selectedDay.value)

    const genreMatch =
      filters.genres.length === 0 || filters.genres.includes(record.genreId)

    return (
      cinemaMatch && roomTypeMatch && screenTypeMatch && dateMatch && genreMatch
    )
  })

  return filteredRecords
}

const changeView = (viewType) => {
  currentView.value = viewType
}

const closeFilterModal = () => {
  showMobileFiltersModal.value = false
}

const resetMoviesFilters = () => {
  resetMoviesFiltersOnly()
  emptyInput()
  filterChanged(selectedFilters.value)
}

const resetMoviesFiltersOnly = () => {
  selectedFilters.value = { ...selectedFiltersIniValue }
}

const startSearchMovies = (e) => {
  resetMoviesFiltersOnly()
  handleSearch(e.target.value)
}

function handleSearch(searchInput) {
  // Filter records based on the search input
  const searchResults = records.value.filter((record) =>
    record.title.toLowerCase().includes(searchInput.toLowerCase()),
  )
  // Update the displayed records with search results
  filteredRecords.value = [...searchResults]
}

const emptyInput = () => {
  searchMovieTitle.value = ''
}

const updateSelectedFilters = (filters, scroll = true) => {
  selectedFilters.value = { ...filters }

  emptyInput()

  filterChanged(filters, scroll)
}
</script>
